/**
 * Capacity sheet — live scarcity for the funnel "capacity" block.
 *
 * Generates the current month's weeks (Monday-aligned) and marks each "obsazeno" from the prior
 * Wednesday 18:00 Prague onward (Monday − 5 days + 18h) — so the nearest free week flips to
 * booked every Wednesday evening. Once every week is booked, the sheet rolls to the next month.
 * All client-side; Prague wall-clock via Intl (DST-correct — not a hardcoded offset).
 *
 * PHP (app/blocks/capacity/render.php) renders a static fallback from slots_free with the same
 * structure, so there's no layout shift and a no-JS view still works. This just overwrites the
 * month header + rebuilds the week rows.
 *
 * Markup: [data-capacity][data-weeks][data-label-free][data-label-booked]
 *           [data-capacity-month]  — "měsíc rok" header
 *           [data-capacity-rows]   — week rows
 */

const DAY = 86400000;
const MONTHS_CZ = ['leden', 'únor', 'březen', 'duben', 'květen', 'červen', 'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec'];

// Prague wall-clock "now" as a UTC-based ms value (so week math is DST-agnostic at day scale).
function pragueNow() {
  const f = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Prague',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  const p = {};
  for (const part of f.formatToParts(new Date())) if (part.type !== 'literal') p[part.type] = part.value;
  let h = parseInt(p.hour, 10); if (h === 24) h = 0; // some engines emit "24" for midnight
  return Date.UTC(+p.year, +p.month - 1, +p.day, h, +p.minute, +p.second);
}

// `count` Monday-aligned week starts, from the first Monday on/after the 1st of the month.
function weekStartsFor(year, month, count) {
  const first = new Date(Date.UTC(year, month, 1));
  const fdow = (first.getUTCDay() + 6) % 7; // 0 = Monday
  const firstMon = fdow === 0 ? 1 : 1 + (7 - fdow);
  const arr = [];
  for (let i = 0; i < count; i++) arr.push(Date.UTC(year, month, firstMon + i * 7));
  return arr;
}

// A week is "obsazeno" from the prior Wednesday 18:00 Prague onward.
function cutoff(monday) { return monday - 5 * DAY + 18 * 3600000; }

function fmtRange(monday) {
  const s = new Date(monday);
  const e = new Date(monday + 6 * DAY);
  const sd = s.getUTCDate(), sm = s.getUTCMonth() + 1;
  const ed = e.getUTCDate(), em = e.getUTCMonth() + 1;
  return sm === em ? `${sd}.–${ed}. ${sm}.` : `${sd}. ${sm}. – ${ed}. ${em}.`;
}

function compute(count) {
  const t = pragueNow();
  const d = new Date(t);
  let year = d.getUTCFullYear();
  let month = d.getUTCMonth();

  let starts = weekStartsFor(year, month, count);
  let booked = starts.map((m) => t >= cutoff(m));
  if (booked.every(Boolean)) { // this month is full → show the next one
    month += 1;
    if (month > 11) { month = 0; year += 1; }
    starts = weekStartsFor(year, month, count);
    booked = starts.map((m) => t >= cutoff(m));
  }

  const weeks = starts.map((m, i) => ({ index: i + 1, range: fmtRange(m), booked: booked[i] }));
  const nearest = weeks.findIndex((w) => !w.booked);
  if (nearest >= 0) weeks[nearest].nextFree = true;

  return { weeks, monthLabel: `${MONTHS_CZ[month]} ${year}` };
}

function rowHTML(w, labelFree, labelBooked) {
  let right;
  if (w.booked) {
    right = `<span class="border-dark-500/50 text-dark-500 -rotate-6 rounded border-2 px-2.5 py-0.5 text-xs font-extrabold tracking-[0.2em] uppercase">${labelBooked}</span>`;
  } else if (w.nextFree) {
    right = `<span class="border-success bg-success/10 text-success inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold tracking-wide uppercase"><span class="relative flex size-2"><span class="bg-success absolute inline-flex size-full animate-ping rounded-full opacity-70"></span><span class="bg-success relative inline-flex size-2 rounded-full"></span></span>${labelFree}</span>`;
  } else {
    right = `<span class="text-success text-sm font-bold tracking-wide uppercase">${labelFree}</span>`;
  }
  return `<div class="flex items-center justify-between gap-4 px-6 py-4${w.booked ? '' : ' bg-success/5'}">
      <div class="text-left">
        <span class="block text-sm font-semibold ${w.booked ? 'text-dark-500' : 'text-white'}">${w.index}. týden</span>
        <span class="text-dark-400 block text-xs">${w.range}</span>
      </div>
      ${right}
    </div>`;
}

function hydrate(root) {
  const count = parseInt(root.dataset.weeks, 10) || 4;
  const labelFree = root.dataset.labelFree || 'volno';
  const labelBooked = root.dataset.labelBooked || 'obsazeno';
  const { weeks, monthLabel } = compute(count);

  const monthEl = root.querySelector('[data-capacity-month]');
  if (monthEl) monthEl.textContent = monthLabel;

  const rowsEl = root.querySelector('[data-capacity-rows]');
  if (rowsEl) {
    rowsEl.innerHTML = weeks
      .map((w) => rowHTML(w, labelFree, labelBooked))
      .join('<div class="bg-dark-200 h-px"></div>');
  }
}

export function capacityMeter() {
  document.querySelectorAll('[data-capacity]').forEach(hydrate);
}
